import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Text1 from "@stackje/shared-components/cards/text/Text1";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import type { CommonColor } from "@stackje/common/config";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const textCardFormSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  type: z.string(),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
});

export type TextCardData = z.infer<typeof textCardFormSchema>;

interface TextCardFormProps {
  initialData: TextCardData;
  mode: "add" | "edit";
}

export function useTextCardForm({ initialData, mode }: TextCardFormProps) {
  const form = useForm<TextCardData>({
    resolver: zodResolver(textCardFormSchema),
    defaultValues: initialData,
  });

  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor);

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title;
  const subtitle = watchedValues.subtitle;
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);

  const toggleType = () => {
    form.setValue("type", "text1");
  };

  const formContent = (
    <>
      <div className="space-y-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Text Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter title"
              />
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="subtitle">Subtitle</Label>
          <Controller
            name="subtitle"
            control={form.control}
            render={({ field }) => (
              <SimpleMarkdownEditor
                id="subtitle"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter subtitle"
              />
            )}
          />
        </div>

        <input type="hidden" name="type" value={type} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
      </div>
    </>
  );

  const previewContent = isDirty || mode === "edit" ? (
    <Text1 title={title} subtitle={subtitle} colors={colors} />
  ) : (
    <Text1 title={sampleData.text.title} subtitle={sampleData.text.subtitle} colors={colors} />
  );

  return {
    form,
    formContent,
    previewContent,
    toggleType,
    shuffleColors,
    isDirty,
    handleSubmit: form.handleSubmit,
    formData: {
      title,
      subtitle,
      type,
      colors,
    } as TextCardData,
  };
}
